import React, { useEffect, useRef, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useDevice } from '../hooks/useDevice';
import SharedHeaderContractor from './SharedHeaderContractor';
import ContractorAccountModal from './ContractorAccountModal';
import ContentLimiter from './layout/ContentLimiter';
import { useProgressiveDataService } from '../lib/progressiveDataService';

import {
  ArrowRight,
  BadgeCheck,
  DollarSign,
  Unlock,
  CheckCircle2,
  Users,
  FileText,
  ShieldCheck,
} from 'lucide-react';

const BRAND_GREEN = '#123c3b';

const ContractorLandingPage: React.FC<{ onNavigate: (screen: string) => void }> = ({ onNavigate }) => {
  const { isMobileLayout } = useDevice();
  const [accountOpen, setAccountOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const { user, isSignedIn } = useUser();
  const { processSignup } = useProgressiveDataService();
  const processedRef = useRef(false);

  useEffect(() => {
    const maybeProcessSignup = async () => {
      if (processedRef.current) return;
      if (isSignedIn && user) {
        processedRef.current = true;
        try {
          const { getApiUrl } = await import('../lib/getApiUrl');
          const resp = await fetch(getApiUrl(`/api/contractor-verification/${user.id}`));
          if (resp.status === 404) {
            await processSignup({
              user_id: user.id,
              email: user.emailAddresses?.[0]?.emailAddress || '',
              account_type: 'CONTRACTOR',
            });
          } else {
            localStorage.setItem('cb.usertype', 'contractor');
          }
        } catch (e) {
          console.error('❌ Contractor processSignup failed', e);
        }
      }
    };
    maybeProcessSignup();
  }, [isSignedIn, user, processSignup]);

  const heroFeatureCards = [
    {
      icon: <Unlock size={48} style={{ color: '#fbbf24' }} />,
      title: 'Free to Join',
      desc: 'No subscriptions, no hidden fees. Only pay when you win projects.',
    },
    {
      icon: <DollarSign size={48} style={{ color: '#60a5fa' }} />,
      title: 'Fast Payments',
      desc: 'Secure escrow with milestone-based releases. Get paid as you complete work.',
    },
    {
      icon: <CheckCircle2 size={48} style={{ color: '#34d399' }} />,
      title: 'Ready Projects',
      desc: 'Bid on fully measured projects with verified homeowners ready to hire.',
    },
  ];

  const howItWorks = [
    {
      num: '01',
      icon: <Users size={32} style={{ color: BRAND_GREEN }} />,
      title: 'Create Your Profile',
      desc: 'Sign up and complete your contractor profile with your specialties and service areas.',
    },
    {
      num: '02',
      icon: <BadgeCheck size={32} style={{ color: BRAND_GREEN }} />,
      title: 'Browse Projects',
      desc: 'View available projects with detailed measurements and homeowner requirements.',
    },
    {
      num: '03',
      icon: <FileText size={32} style={{ color: BRAND_GREEN }} />,
      title: 'Submit Your Bid',
      desc: 'Send competitive proposals directly to homeowners ready to hire.',
    },
    {
      num: '04',
      icon: <ShieldCheck size={32} style={{ color: BRAND_GREEN }} />,
      title: 'Get Paid Securely',
      desc: 'Complete the work and receive milestone payments through our secure escrow system.',
    },
  ];

  const contractorTestimonials = [
    {
      quote:
        '"CoreBids helped me grow my business by 40% in just 6 months. The quality of leads is outstanding!"',
      name: 'Mike R.',
      company: 'Nashville Roofing Co.',
    },
    {
      quote:
        '"The platform is so easy to use. I can submit bids in minutes and track all my projects in one place."',
      name: 'Sarah L.',
      company: 'Premier Contractors',
    },
    {
      quote:
        '"Best decision I made for my contracting business. The payment system is fast and reliable."',
      name: 'David K.',
      company: 'Elite Construction',
    },
  ];

  const handlePrimaryCta = () => onNavigate('contractor-signup');
  const handleBackHome = () => onNavigate('landing');

  return (
    <div style={{ fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, sans-serif', background: '#fff' }}>
      <style>{`
        .cb-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(0,0,0,0.25); }
        .cb-btn-secondary:hover { background-color: rgba(255,255,255,0.10); border-color: rgba(255,255,255,0.50); }
        .cb-feature-card:hover { background-color: rgba(255,255,255,0.25); transform: translateY(-4px); }
        .cb-step-card:hover { border-color: ${BRAND_GREEN}; transform: translateY(-4px); box-shadow: 0 12px 32px rgba(18,60,59,0.10); }
      `}</style>

      <SharedHeaderContractor onNavigate={onNavigate} />

      <section
        style={{
            position: 'relative',
          padding: isMobileLayout ? '56px 16px 64px' : '100px 40px 120px',
          background: 'linear-gradient(135deg, #123c3b 0%, #0f5251 50%, #1a6b69 100%)',
            overflow: 'hidden',
          }}
        >
        <div
          style={{
          position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 20% 80%, rgba(230, 245, 239, 0.10) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(230, 245, 239, 0.10) 0%, transparent 50%)',
            pointerEvents: 'none',
          }}
        />

        <ContentLimiter>
          <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', maxWidth: 1000, margin: '0 auto' }}>
            <h1
          style={{
                fontSize: isMobileLayout ? 40 : 'clamp(48px, 6vw, 72px)',
                fontWeight: 900,
                color: '#fff',
                marginBottom: 24,
                lineHeight: 1.1,
                textShadow: '0 4px 20px rgba(0,0,0,0.2)',
                letterSpacing: '-0.02em',
              }}
            >
              Welcome,{' '}
              <span
                style={{
                  background: 'linear-gradient(135deg, #e6f5ef, #a7f3d0)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Contractors!
              </span>
            </h1>
            
            <p
              style={{
                fontSize: isMobileLayout ? 18 : 24,
                color: 'rgba(255,255,255,0.9)',
                lineHeight: 1.6,
                maxWidth: 700,
                margin: '0 auto 48px',
              }}
            >
              Join CoreBids and connect with homeowners ready to hire. No subscriptions, just qualified leads.
            </p>

            <div
              style={{
                display: 'flex',
                gap: 16,
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: isMobileLayout ? 40 : 64,
                flexWrap: 'wrap',
              }}
            >
              <button
                className="cb-btn-primary"
                onClick={handlePrimaryCta}
                style={{
                  fontSize: isMobileLayout ? 18 : 20,
                  fontWeight: 700,
                  padding: isMobileLayout ? '16px 22px' : '20px 48px',
                  backgroundColor: '#fff',
                  color: BRAND_GREEN,
                  border: 'none',
                  borderRadius: 12,
                  cursor: 'pointer',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  minHeight: 44,
                }}
              >
                Start Earning Today <ArrowRight size={20} />
              </button>

              <button
                className="cb-btn-secondary"
                onClick={handleBackHome}
                style={{
                  fontSize: isMobileLayout ? 16 : 18,
                  fontWeight: 600,
                  padding: isMobileLayout ? '14px 18px' : '18px 40px',
                  backgroundColor: 'transparent',
                  color: '#fff',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderRadius: 12,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minHeight: 44,
                }}
              >
                ← Back to Home
              </button>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: 24,
                maxWidth: 1000,
                margin: '0 auto',
              }}
            >
              {heroFeatureCards.map((c) => (
                <div
                  key={c.title}
                  className="cb-feature-card"
                  style={{
                    padding: '32px 24px',
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    borderRadius: 20,
                    border: '1px solid rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <div
                    style={{
                      display: 'inline-flex',
                      padding: 16,
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      borderRadius: 16,
                      marginBottom: 20,
                    }}
                    aria-hidden="true"
                  >
                    {c.icon}
                  </div>
                  <h3 style={{ fontSize: 24, fontWeight: 700, color: '#fff', marginBottom: 12 }}>{c.title}</h3>
                  <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>{c.desc}</p>
                </div>
              ))}
            </div>
        </div>
        </ContentLimiter>
      </section>

      <section style={{ padding: isMobileLayout ? '64px 16px' : '100px 40px', backgroundColor: '#fff' }}>
        <ContentLimiter>
          <div style={{ maxWidth: 1000, margin: '0 auto' }}>
            <h2 style={{ fontSize: isMobileLayout ? 34 : 48, fontWeight: 900, color: '#0f172a', textAlign: 'center' }}>
              How It Works
            </h2>
            <p
              style={{
                fontSize: 18,
                color: '#64748b',
            textAlign: 'center', 
                margin: '16px auto 64px',
                maxWidth: 600,
              }}
            >
              Start winning projects in four simple steps
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: 32,
              }}
            >
              {howItWorks.map((s) => (
                <div
                  key={s.num}
                  className="cb-step-card"
                  style={{
                    display: 'flex',
                    gap: 24,
                    padding: 32,
                    backgroundColor: '#f8fafc',
                    borderRadius: 20,
                    border: '2px solid #e2e8f0',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <div
                    style={{
                      fontSize: 48,
                      fontWeight: 900,
                      color: '#e6f5ef',
                      lineHeight: 1,
                      minWidth: 60,
                    }}
                    aria-hidden="true"
                  >
                    {s.num}
                  </div>

                  <div>
                    <div style={{ marginBottom: 12 }} aria-hidden="true">
                      {s.icon}
                    </div>
                    <h3 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>{s.title}</h3>
                    <p style={{ fontSize: 16, color: '#64748b', lineHeight: 1.6 }}>{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ContentLimiter>
      </section>

      <section style={{ padding: isMobileLayout ? '56px 16px' : '80px 40px', backgroundColor: '#e6f5ef' }}>
        <ContentLimiter>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 40,
              maxWidth: 1000,
              margin: '0 auto',
              textAlign: 'center',
            }}
          >
            {[
              { big: '100%', label: 'Free to Join' },
              { big: 'Fast', label: 'Secure Payments' },
              { big: '24/7', label: 'Platform Access' },
            ].map((x) => (
              <div key={x.label}>
                <div style={{ fontSize: isMobileLayout ? 52 : 64, fontWeight: 900, color: BRAND_GREEN, marginBottom: 8 }}>
                  {x.big}
                </div>
                <div style={{ fontSize: 18, fontWeight: 600, color: '#334155' }}>{x.label}</div>
              </div>
            ))}
          </div>
        </ContentLimiter>
      </section>

      <section style={{ padding: isMobileLayout ? '64px 16px' : '100px 40px', backgroundColor: '#fff' }}>
        <ContentLimiter>
          <div style={{ maxWidth: 1000, margin: '0 auto' }}>
            <h2 style={{ fontSize: isMobileLayout ? 34 : 48, fontWeight: 900, color: '#0f172a', textAlign: 'center', marginBottom: 64 }}>
              What Contractors Say
            </h2>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: 24,
              }}
            >
              {contractorTestimonials.map((t) => (
                <div
                  key={t.name}
                  style={{
                    padding: 32,
                    backgroundColor: '#fff',
                    borderRadius: 20,
                    border: '2px solid #e2e8f0',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  }}
                >
                  <div style={{ fontSize: 24, color: '#fbbf24', marginBottom: 16 }} aria-hidden="true">
                    ★★★★★
                  </div>
                  <p style={{ fontSize: 15, fontStyle: 'italic', color: '#475569', lineHeight: 1.7, marginBottom: 20 }}>
                    {t.quote}
                  </p>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: BRAND_GREEN }}>{t.name}</div>
                    <div style={{ fontSize: 14, color: '#64748b' }}>{t.company}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ContentLimiter>
      </section>

      {/* CB:BEGIN contractor-faq-section */}
      <section id="contractor-faq" style={{ padding: isMobileLayout ? '64px 16px' : '100px 40px', backgroundColor: '#fff' }}>
        <ContentLimiter>
          <div style={{ maxWidth: 1000, margin: '0 auto' }}>
            <h2 style={{ fontSize: isMobileLayout ? 34 : 48, fontWeight: 900, color: '#0f172a', textAlign: 'center', marginBottom: 16 }}>
              Contractor FAQs
            </h2>
            <p style={{ fontSize: 18, color: '#64748b', textAlign: 'center', marginBottom: 64, maxWidth: 600, margin: '16px auto 64px' }}>
              Everything you need to know before reviewing your first verified project.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                {
                  q: 'How does the CoreBids process work?',
                  a: 'The process is simple: (1) Review a verified project with address redacted—you\'ll see project details, measurements, and homeowner requirements. (2) Submit a bid in minutes with your price, timeline, warranty summary, and optional notes. (3) The homeowner reviews all bids and chooses who to move forward with. (4) If you win: complete onboarding, set up Stripe Connect for payments, and submit required documents. You only pay if you win, and you get the roof report (worth $50–$100+) included with your win.'
                },
                {
                  q: 'Is this a lead list?',
                  a: 'No. CoreBids is built around real homeowner requests. The homeowner has already asked for bids, and we often include a professional roof measurement report to support accurate quoting. This is about speed to close, not "exposure."'
                },
                {
                  q: 'What\'s the catch?',
                  a: 'There isn\'t one. We limit bidding to 2–3 contractors per project so bids are worth your time. If it\'s not a fit, you can pass—no penalty.'
                },
                {
                  q: 'Do I pay anything to bid?',
                  a: 'No upfront cost. You only pay if you win the project. No subscription required to review.'
                },
                {
                  q: 'How many contractors are invited to bid?',
                  a: 'Typically 2–3. We keep competition limited to reduce wasted time and improve close rates.'
                },
                {
                  q: 'Do I get the roof report?',
                  a: 'If you win the job, the report is yours to keep (a $50–$100+ value). If you don\'t win, you don\'t pay.'
                },
                {
                  q: 'What do I need to submit a bid?',
                  a: 'Price, timeline, warranty summary, and optional notes. We keep it simple so you can quote quickly.'
                },
                {
                  q: 'What info do I need to provide up front?',
                  a: 'Minimal details only: business name, contact info, service area/radius, and a license/insurance confirmation checkbox. Full onboarding happens only if you win.'
                },
                {
                  q: 'When do I need to complete full onboarding and payments setup?',
                  a: 'Only after you win a project. That\'s when we collect full profile details, Stripe Connect setup, and required documents.'
                },
                {
                  q: 'What locations do you serve?',
                  a: 'Nationwide. Projects are matched by service radius and availability in your area.'
                },
                {
                  q: 'What if a project gets no bids?',
                  a: 'We expand the radius automatically after ~24 hours and notify ops for manual outreach to ensure homeowners get coverage.'
                }
              ].map((faq, idx) => {
                const isOpen = openFaqIndex === idx;
                const isHowItWorks = idx === 0;
                return (
                  <div
                    key={idx}
                    id={isHowItWorks ? 'how-it-works-faq' : undefined}
                    style={{
                      background: '#ffffff',
                      border: `2px solid ${isOpen ? BRAND_GREEN : '#e2e8f0'}`,
                      borderRadius: '16px',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      scrollMarginTop: '80px'
                    }}
                  >
                    <button
                      onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: isMobileLayout ? '20px' : '24px',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                      aria-expanded={isOpen}
                    >
                      <span style={{
                        fontSize: isMobileLayout ? 16 : 18,
                        fontWeight: 600,
                        color: '#0f172a',
                        flex: 1,
                        paddingRight: 16
                      }}>
                        {faq.q}
                      </span>
                      <span style={{
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        color: BRAND_GREEN,
                        flexShrink: 0,
                        minWidth: 24,
                        textAlign: 'center'
                      }}>
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>

                    {isOpen && (
                      <div style={{
                        padding: `0 ${isMobileLayout ? '20px' : '24px'} ${isMobileLayout ? '20px' : '24px'} ${isMobileLayout ? '20px' : '24px'}`,
                        fontSize: isMobileLayout ? 15 : 16,
                        color: '#64748b',
                        lineHeight: 1.6
                      }}>
                        {faq.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </ContentLimiter>
      </section>
      {/* CB:END contractor-faq-section */}

      <section
        style={{
          padding: isMobileLayout ? '72px 16px' : '100px 40px',
          background: 'linear-gradient(135deg, #123c3b 0%, #0f5251 100%)',
          textAlign: 'center',
        }}
      >
        <ContentLimiter>
          <div style={{ maxWidth: 700, margin: '0 auto' }}>
            <h2 style={{ fontSize: isMobileLayout ? 34 : 48, fontWeight: 900, color: '#fff', marginBottom: 24, lineHeight: 1.2 }}>
              Ready to Grow Your Business?
            </h2>
            <p style={{ fontSize: isMobileLayout ? 18 : 20, color: 'rgba(255,255,255,0.9)', marginBottom: 40, lineHeight: 1.6 }}>
              Join hundreds of contractors already winning projects on CoreBids
            </p>

            <button
              className="cb-btn-primary"
              onClick={handlePrimaryCta}
              style={{
                fontSize: isMobileLayout ? 18 : 20,
                fontWeight: 700,
                padding: isMobileLayout ? '16px 22px' : '20px 48px',
                backgroundColor: '#fff',
                color: BRAND_GREEN,
                border: 'none',
                borderRadius: 12,
                cursor: 'pointer',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                transition: 'all 0.3s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
                minHeight: 44,
              }}
            >
              Get Started Free <ArrowRight size={22} />
            </button>
        </div>
        </ContentLimiter>
      </section>

      <ContractorAccountModal open={accountOpen} onClose={() => setAccountOpen(false)} />
    </div>
  );
};

export default ContractorLandingPage;
